/**
 * Cognito auth helpers.
 *
 * Only activated when REACT_APP_AUTH_PROVIDER=cognito.
 * All other environments continue to use the local /api/token flow unchanged.
 *
 * Public API:
 *   cognitoSignIn(email, password)  → { idToken, refreshToken }
 *   cognitoSignOut()
 *   cognitoRefreshSession(refreshToken) → { idToken, refreshToken }
 *   cognitoForgotPassword(email)
 *   cognitoResetPassword(email, code, newPassword)
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";

const USER_POOL_ID = process.env.REACT_APP_COGNITO_USER_POOL_ID || "";
const CLIENT_ID = process.env.REACT_APP_COGNITO_APP_CLIENT_ID || "";

export const isCognitoEnabled =
  process.env.REACT_APP_AUTH_PROVIDER === "cognito";

let _pool = null;

function getPool() {
  if (!_pool) {
    if (!USER_POOL_ID || !CLIENT_ID) {
      throw new Error(
        "Cognito is enabled (REACT_APP_AUTH_PROVIDER=cognito) but " +
          "REACT_APP_COGNITO_USER_POOL_ID or REACT_APP_COGNITO_APP_CLIENT_ID is missing."
      );
    }
    // `Storage: sessionStorage` para que la copia del SDK (idToken, refreshToken,
    // LastAuthUser) muera al cerrar la pestaña, igual que nuestras claves.
    _pool = new CognitoUserPool({
      UserPoolId: USER_POOL_ID,
      ClientId: CLIENT_ID,
      Storage: window.sessionStorage,
    });
  }
  return _pool;
}

function makeCognitoUser(email) {
  // En v6 el CognitoUser NO hereda el Storage del pool: hay que repetirlo aquí o
  // `authenticateUser`/`refreshSession` volverían a escribir en localStorage.
  return new CognitoUser({
    Username: email,
    Pool: getPool(),
    Storage: window.sessionStorage,
  });
}

/**
 * Sign in with email + password.
 * Returns { idToken, accessToken, refreshToken } on success.
 * Throws a localized Error on failure.
 */
export function cognitoSignIn(email, password) {
  return new Promise((resolve, reject) => {
    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });
    const user = makeCognitoUser(email);

    user.authenticateUser(authDetails, {
      onSuccess(session) {
        resolve({
          idToken: session.getIdToken().getJwtToken(),
          accessToken: session.getAccessToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
        });
      },
      onFailure(err) {
        reject(translateCognitoError(err));
      },
      newPasswordRequired() {
        reject(new Error("Se requiere cambio de contraseña. Contacta al administrador."));
      },
    });
  });
}

/**
 * Sign out the currently stored Cognito user. LOCAL sign-out: limpia lo guardado en
 * este dispositivo, pero NO revoca el refresh token en el servidor (sigue válido hasta
 * que caduque).
 */
export function cognitoSignOut() {
  const pool = getPool();
  const user = pool.getCurrentUser();
  if (user) {
    user.signOut();
  }
}

/**
 * Refresh the Cognito session using a stored refresh token.
 * Returns { idToken, accessToken, refreshToken } on success.
 */
export function cognitoRefreshSession(email, refreshToken) {
  const { CognitoRefreshToken } = require("amazon-cognito-identity-js");
  return new Promise((resolve, reject) => {
    const user = makeCognitoUser(email);
    const token = new CognitoRefreshToken({ RefreshToken: refreshToken });

    user.refreshSession(token, (err, session) => {
      if (err) {
        reject(translateCognitoError(err));
      } else {
        resolve({
          idToken: session.getIdToken().getJwtToken(),
          accessToken: session.getAccessToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
        });
      }
    });
  });
}

/**
 * Trigger the "forgot password" flow — Cognito sends a verification code.
 */
export function cognitoForgotPassword(email) {
  return new Promise((resolve, reject) => {
    const user = makeCognitoUser(email);
    user.forgotPassword({
      onSuccess() {
        resolve();
      },
      onFailure(err) {
        reject(translateCognitoError(err));
      },
    });
  });
}

/**
 * Confirm a new password using the code sent by cognitoForgotPassword.
 */
export function cognitoResetPassword(email, code, newPassword) {
  return new Promise((resolve, reject) => {
    const user = makeCognitoUser(email);
    user.confirmPassword(code, newPassword, {
      onSuccess() {
        resolve();
      },
      onFailure(err) {
        reject(translateCognitoError(err));
      },
    });
  });
}

// ─── Error translation ────────────────────────────────────────────────────────

const COGNITO_ERROR_MAP = {
  NotAuthorizedException: "Email o contraseña incorrectos.",
  UserNotFoundException: "No existe una cuenta con ese email.",
  UserNotConfirmedException: "La cuenta no está confirmada. Revisa tu email.",
  PasswordResetRequiredException: "Debes restablecer tu contraseña antes de continuar.",
  ExpiredCodeException: "El código ha expirado. Solicita uno nuevo.",
  CodeMismatchException: "El código introducido no es correcto.",
  LimitExceededException: "Demasiados intentos. Espera un momento e inténtalo de nuevo.",
  TooManyRequestsException: "Demasiados intentos. Espera un momento e inténtalo de nuevo.",
  InvalidPasswordException: "La contraseña no cumple los requisitos de seguridad.",
};

function translateCognitoError(err) {
  const msg = COGNITO_ERROR_MAP[err.code] || err.message || "Error de autenticación.";
  const translated = new Error(msg);
  translated.code = err.code;
  return translated;
}
